import {
  articleList,
  discoverRequest,
  discoverResponse,
  feedErrorList,
  feedList,
  health,
  ruleEventList,
  type ruleEventsQuery,
  ruleInput,
  ruleList,
  removeArticlesRequest,
  routes,
  type searchQuery,
  setEnabledRequest,
  setKeptRequest,
  setReadRequest,
  subscribeRequest,
} from "@newsfeed/shared";
import type {
  ArticlesQuery,
  Article,
  Feed,
  FeedCandidate,
  FeedError,
  Rule,
  RuleEvent,
  RuleInput,
} from "@newsfeed/shared";
import type { z } from "zod";

type Health = z.infer<typeof health>;
type SearchQuery = z.infer<typeof searchQuery>;
type RuleEventsQuery = z.infer<typeof ruleEventsQuery>;

/** Thrown for any non-2xx API response; carries status + parsed error body. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    override readonly message: string,
    readonly detail?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface ApiOptions {
  apiBase: string;
  getToken: () => Promise<string>;
}

type RouteDef = (typeof routes)[keyof typeof routes];

interface RequestSpec<S extends z.ZodTypeAny | undefined> {
  params?: Record<string, string>;
  query?: Record<string, string | number | undefined>;
  body?: unknown;
  schema?: S;
}

export class ApiClient {
  constructor(private readonly opts: ApiOptions) {}

  private buildUrl(
    route: RouteDef,
    spec: RequestSpec<z.ZodTypeAny | undefined>,
  ): string {
    let path: string = route.path;
    for (const [key, value] of Object.entries(spec.params ?? {})) {
      path = path.replace(`:${key}`, encodeURIComponent(value));
    }
    const url = new URL(this.opts.apiBase + path, window.location.origin);
    for (const [key, value] of Object.entries(spec.query ?? {})) {
      if (value !== undefined && value !== "")
        url.searchParams.set(key, String(value));
    }
    return url.toString();
  }

  private async request<S extends z.ZodTypeAny | undefined>(
    route: RouteDef,
    spec: RequestSpec<S> = {},
  ): Promise<[S] extends [z.ZodTypeAny] ? z.infer<S> : void> {
    const headers: Record<string, string> = {};
    if (route.auth)
      headers.Authorization = `Bearer ${await this.opts.getToken()}`;
    if (spec.body !== undefined) headers["Content-Type"] = "application/json";

    const res = await fetch(this.buildUrl(route, spec), {
      method: route.method,
      headers,
      body: spec.body === undefined ? undefined : JSON.stringify(spec.body),
    });

    if (!res.ok) {
      let message = `${route.method} ${route.path} failed (${res.status})`;
      let detail: string | undefined;
      try {
        const body = (await res.json()) as { error?: string; detail?: string };
        if (body?.error) message = body.error;
        detail = body?.detail;
      } catch {
        /* non-JSON error body */
      }
      throw new ApiError(res.status, message, detail);
    }

    if (!spec.schema) return undefined as never;
    return spec.schema.parse(await res.json()) as [S] extends [z.ZodTypeAny]
      ? z.infer<S>
      : void;
  }

  health(): Promise<Health> {
    return this.request(routes.health, { schema: health });
  }

  listFeeds(): Promise<Feed[]> {
    return this.request(routes.listFeeds, { schema: feedList });
  }

  listFeedErrors(id: string): Promise<FeedError[]> {
    return this.request(routes.listFeedErrors, {
      params: { id },
      schema: feedErrorList,
    });
  }

  subscribe(url: string): Promise<void> {
    return this.request(routes.subscribe, {
      body: subscribeRequest.parse({ url }),
    });
  }

  discoverFeeds(url: string): Promise<FeedCandidate[]> {
    return this.request(routes.discover, {
      body: discoverRequest.parse({ url }),
      schema: discoverResponse,
    }).then((res) => res.candidates);
  }

  unsubscribe(id: string): Promise<void> {
    return this.request(routes.unsubscribe, { params: { id } });
  }

  listArticles(query: Partial<ArticlesQuery> = {}): Promise<Article[]> {
    return this.request(routes.listArticles, {
      query: {
        feed: query.feed,
        state: query.state,
        limit: query.limit,
        cursor: query.cursor,
      },
      schema: articleList,
    });
  }

  searchArticles(query: SearchQuery): Promise<Article[]> {
    return this.request(routes.searchArticles, {
      query: { q: query.q, limit: query.limit },
      schema: articleList,
    });
  }

  keptArticles(): Promise<Article[]> {
    return this.request(routes.keptArticles, { schema: articleList });
  }

  setRead(id: string, read: boolean): Promise<void> {
    return this.request(routes.setRead, {
      params: { id },
      body: setReadRequest.parse({ read }),
    });
  }

  openArticle(id: string): Promise<void> {
    return this.request(routes.openArticle, { params: { id } });
  }

  setKept(id: string, kept: boolean): Promise<void> {
    return this.request(routes.setKept, {
      params: { id },
      body: setKeptRequest.parse({ kept }),
    });
  }

  removeArticles(ids: string[]): Promise<void> {
    return this.request(routes.removeArticles, {
      body: removeArticlesRequest.parse({ ids }),
    });
  }

  listRules(): Promise<Rule[]> {
    return this.request(routes.listRules, { schema: ruleList });
  }

  createRule(input: RuleInput): Promise<void> {
    return this.request(routes.createRule, { body: ruleInput.parse(input) });
  }

  updateRule(id: string, input: RuleInput): Promise<void> {
    return this.request(routes.updateRule, {
      params: { id },
      body: ruleInput.parse(input),
    });
  }

  deleteRule(id: string): Promise<void> {
    return this.request(routes.deleteRule, { params: { id } });
  }

  setRuleEnabled(id: string, enabled: boolean): Promise<void> {
    return this.request(routes.setRuleEnabled, {
      params: { id },
      body: setEnabledRequest.parse({ enabled }),
    });
  }

  listRuleEvents(query: Partial<RuleEventsQuery> = {}): Promise<RuleEvent[]> {
    return this.request(routes.listRuleEvents, {
      query: {
        rule: query.rule,
        feed: query.feed,
        outcome: query.outcome,
        since: query.since,
        until: query.until,
        limit: query.limit,
      },
      schema: ruleEventList,
    });
  }
}
